/**
 * 2D Canvas for warehouse layout editing.
 * Uses context for state, composable renderers for drawing, and conditional animation loop.
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import {
  RackPosition,
  LocationActivity,
  HeatmapData,
} from '../types/warehouseLayout';
import {
  snapToGrid,
  screenToWorld,
  getRotatedDimensions,
} from '../utils/positionCalculator';
import {
  isMultiSelectKey,
  isRangeSelectKey,
  getRacksInArea,
  toggleSelection,
  singleSelect,
  rangeSelect,
  startRubberBand,
  updateRubberBand,
  resetRubberBand,
} from '../utils/selectionUtils';
import {
  computeRenderContext,
  drawBackground,
  drawFloor,
  drawGrid,
  drawBackgroundImage,
  drawDimensionLabels,
  drawObjects,
  drawBinPositions,
  drawPreviewRacks,
  drawRacks,
  drawRubberBand,
  drawOverlays,
} from '../utils/canvasRenderers';
import { resolveBinRenderInfo, PlacedBinRenderInfo } from '../utils/layoutApiMapping';

interface DragState {
  isDragging: boolean;
  draggedRacks: string[];
  startPositions: Map<string, { x: number; y: number }>;
  mouseStartX: number;
  mouseStartY: number;
}

/** Drag-tilstand for enkeltlokasjoner (BinPositions) — separat fra reol-drag */
interface BinDragState {
  isDragging: boolean;
  binCode: string | null;
  startX: number;
  startY: number;
  mouseStartX: number;
  mouseStartY: number;
}

const IDLE_BIN_DRAG: BinDragState = {
  isDragging: false,
  binCode: null,
  startX: 0,
  startY: 0,
  mouseStartX: 0,
  mouseStartY: 0,
};

const Layout2DCanvas: React.FC = () => {
  const { state, dispatch, rackDimensionsMap, backgroundUrl } = useLayoutEditor();
  const { layout, editorState, previewPositions } = state;

  // Plantegning-underlag: last bilde-element når URL endres
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!backgroundUrl) {
      setBackgroundImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setBackgroundImage(img);
    img.src = backgroundUrl;
  }, [backgroundUrl]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedRacks: [],
    startPositions: new Map(),
    mouseStartX: 0,
    mouseStartY: 0,
  });
  const [binDragState, setBinDragState] = useState<BinDragState>(IDLE_BIN_DRAG);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastClickedRack, setLastClickedRack] = useState<string | null>(null);

  // Stub data (Phase 2 integration)
  const heatmapData: HeatmapData[] = [];
  const activities: LocationActivity[] = [];

  // Enkeltlokasjoner (gulv/vegg/heng/skap/dock) med mål fra StcBinSize
  const binRenderInfos = useMemo(() => {
    const specialByCode = new Map(state.specialBins.map(b => [b.binCode, b]));
    return state.binPositions.map(p => resolveBinRenderInfo(p, specialByCode));
  }, [state.binPositions, state.specialBins]);

  // Create lookups
  const heatmapLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    heatmapData.forEach(data => {
      lookup.set(data.rackNumber, Math.max(lookup.get(data.rackNumber) || 0, data.intensity));
    });
    return lookup;
  }, [heatmapData]);

  const activityLookup = useMemo(() => {
    const lookup = new Map<string, LocationActivity[]>();
    activities.forEach(activity => {
      const key = activity.rackNumber;
      if (!lookup.has(key)) lookup.set(key, []);
      lookup.get(key)!.push(activity);
    });
    return lookup;
  }, [activities]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!layout) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ikke stjel tastetrykk fra skjemafelter (egenskaps-panel, søk, innstillinger)
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      // Undo (Ctrl+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
        return;
      }

      // Redo (Ctrl+Shift+Z or Ctrl+Y)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || e.key === 'y') && (e.shiftKey || e.key === 'y')) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
        return;
      }

      // Select all (Ctrl+A)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        dispatch({ type: 'SET_SELECTION', selectedRacks: new Set(layout.racks.map(r => r.rackNumber)) });
      }

      // Deselect all (Escape)
      if (e.key === 'Escape') {
        dispatch({ type: 'SET_SELECTION', selectedRacks: new Set() });
        if (state.selectedBinCode) {
          dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
        }
        if (editorState.rubberBand.isActive) {
          dispatch({ type: 'SET_RUBBER_BAND', rubberBand: resetRubberBand() });
        }
      }

      // Enkeltlokasjon valgt: R = roter, Delete = fjern plasseringen (ikke lokasjonen)
      if (state.selectedBinCode) {
        if (e.key === 'r' && !e.ctrlKey && !e.metaKey) {
          dispatch({ type: 'ROTATE_BIN', binCode: state.selectedBinCode });
          return;
        }
        if (e.key === 'Delete') {
          dispatch({ type: 'REMOVE_BIN', binCode: state.selectedBinCode });
          return;
        }
      }

      // Delete selected (Delete)
      if (e.key === 'Delete' && editorState.selectedRacks.size > 0) {
        dispatch({ type: 'DELETE_SELECTED' });
      }

      // Rotate selected (R)
      if (e.key === 'r' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'ROTATE_SELECTED' });
      }

      // Align to grid (G)
      if (e.key === 'g' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'ALIGN_SELECTED_TO_GRID' });
      }

      // Arrow keys to nudge selected racks
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && editorState.selectedRacks.size > 0) {
        e.preventDefault();
        const step = layout.gridUnit;
        const updates: Array<{ rackNumber: string; x: number; y: number }> = [];
        layout.racks.forEach(rack => {
          if (editorState.selectedRacks.has(rack.rackNumber)) {
            let dx = 0, dy = 0;
            if (e.key === 'ArrowLeft') dx = -step;
            if (e.key === 'ArrowRight') dx = step;
            if (e.key === 'ArrowUp') dy = -step;
            if (e.key === 'ArrowDown') dy = step;
            updates.push({
              rackNumber: rack.rackNumber,
              x: snapToGrid(rack.x + dx, layout.gridUnit),
              y: snapToGrid(rack.y + dy, layout.gridUnit),
            });
          }
        });
        // Push history for arrow nudge, then move
        dispatch({ type: 'MOVE_RACKS', updates });
      }

      // Zoom with + / -
      if (e.key === '+' || e.key === '=') {
        dispatch({ type: 'SET_ZOOM', zoom: Math.min(5, editorState.zoom * 1.1) });
      }
      if (e.key === '-') {
        dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, editorState.zoom * 0.9) });
      }

      // Reset view (H)
      if (e.key === 'h' && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'SET_ZOOM', zoom: 1 });
        dispatch({ type: 'SET_PAN_OFFSET', offset: { x: 0, y: 0 } });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editorState, layout, dispatch, state.selectedBinCode]);

  // Draw canvas using composable renderers
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layout) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rc = computeRenderContext(
      ctx, canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    drawBackground(rc);
    drawFloor(rc, layout.dimensions);
    if (backgroundImage) drawBackgroundImage(rc, backgroundImage, layout.dimensions);
    if (editorState.showGrid) drawGrid(rc, layout.dimensions, layout.gridUnit);
    drawDimensionLabels(rc, layout.dimensions);
    drawObjects(rc, layout.objects, editorState.showLabels);
    drawBinPositions(rc, binRenderInfos, editorState.showLabels, state.selectedBinCode);
    // Render bulk preview positions + sidebar drag ghost
    const allPreviews = state.sidebarDragPreview
      ? [...previewPositions, state.sidebarDragPreview]
      : previewPositions;
    drawPreviewRacks(rc, allPreviews, rackDimensionsMap);
    drawRacks(
      rc, layout.racks, rackDimensionsMap,
      editorState.selectedRacks, editorState.anchorRack,
      editorState.showLabels, editorState.showHeatmap, editorState.showActivity,
      heatmapLookup, activityLookup
    );
    drawRubberBand(rc, editorState.rubberBand);
    drawOverlays(rc, rc.scale);
  }, [layout, editorState, canvasSize, previewPositions, rackDimensionsMap, heatmapLookup, activityLookup, binRenderInfos, backgroundImage, state.selectedBinCode]);

  // Redraw on changes - conditional animation loop
  useEffect(() => {
    draw();

    const hasActiveAnimations = editorState.showActivity && activities.length > 0;
    if (!hasActiveAnimations) return;

    let animationId: number;
    let lastFrameTime = 0;
    const targetFps = 30;
    const frameInterval = 1000 / targetFps;

    const animate = (timestamp: number) => {
      if (timestamp - lastFrameTime >= frameInterval) {
        draw();
        lastFrameTime = timestamp;
      }
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [draw, editorState.showActivity, activities.length]);

  // Get rack at screen position
  const getRackAtPosition = (screenX: number, screenY: number): RackPosition | null => {
    if (!layout) return null;
    const world = screenToWorld(
      screenX, screenY,
      canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    for (const rack of layout.racks) {
      const dim = rackDimensionsMap.get(rack.rackNumber) || { width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5 };
      const rotated = getRotatedDimensions(dim, rack.rotation);
      if (
        world.x >= rack.x - rotated.width / 2 &&
        world.x <= rack.x + rotated.width / 2 &&
        world.y >= rack.y - rotated.depth / 2 &&
        world.y <= rack.y + rotated.depth / 2
      ) {
        return rack;
      }
    }
    return null;
  };

  // Hit-test for enkeltlokasjoner — samme world-koordinat-logikk som reolene.
  // Kalles ETTER getRackAtPosition (reoler har prioritet ved overlapp).
  const getBinAtPosition = (screenX: number, screenY: number): PlacedBinRenderInfo | null => {
    if (!layout) return null;
    const world = screenToWorld(
      screenX, screenY,
      canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    for (const bin of binRenderInfos) {
      const rotated = (bin.rotation === 90 || bin.rotation === 270)
        ? { width: bin.depthM, depth: bin.widthM }
        : { width: bin.widthM, depth: bin.depthM };
      if (
        world.x >= bin.x - rotated.width / 2 &&
        world.x <= bin.x + rotated.width / 2 &&
        world.y >= bin.y - rotated.depth / 2 &&
        world.y <= bin.y + rotated.depth / 2
      ) {
        return bin;
      }
    }
    return null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!layout) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Right-click or middle-click for panning
    if (e.button === 1 || e.button === 2) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }
    if (e.button !== 0) return;

    const clickedRack = getRackAtPosition(screenX, screenY);
    const isMultiSelect = isMultiSelectKey(e);
    const isRangeSelect_ = isRangeSelectKey(e);

    if (clickedRack) {
      let newSelection: Set<string>;
      if (isMultiSelect) {
        newSelection = toggleSelection(editorState.selectedRacks, clickedRack.rackNumber);
      } else if (isRangeSelect_ && lastClickedRack) {
        newSelection = rangeSelect(editorState.selectedRacks, layout.racks, lastClickedRack, clickedRack.rackNumber);
      } else {
        newSelection = singleSelect(clickedRack.rackNumber);
      }

      dispatch({ type: 'SET_SELECTION', selectedRacks: newSelection });
      setLastClickedRack(clickedRack.rackNumber);

      // Start drag
      if (newSelection.has(clickedRack.rackNumber)) {
        const world = screenToWorld(
          screenX, screenY,
          canvasSize.width, canvasSize.height,
          layout.dimensions, editorState.zoom, editorState.panOffset
        );
        const startPositions = new Map<string, { x: number; y: number }>();
        Array.from(newSelection).forEach(rackNum => {
          const rack = layout.racks.find(r => r.rackNumber === rackNum);
          if (rack) startPositions.set(rackNum, { x: rack.x, y: rack.y });
        });
        setDragState({
          isDragging: true,
          draggedRacks: Array.from(newSelection),
          startPositions,
          mouseStartX: world.x,
          mouseStartY: world.y,
        });
      }
    } else {
      // Ingen reol truffet — sjekk enkeltlokasjoner (reoler har prioritet)
      const clickedBin = getBinAtPosition(screenX, screenY);
      if (clickedBin) {
        dispatch({ type: 'SET_SELECTED_BIN', binCode: clickedBin.binCode });
        const world = screenToWorld(
          screenX, screenY,
          canvasSize.width, canvasSize.height,
          layout.dimensions, editorState.zoom, editorState.panOffset
        );
        setBinDragState({
          isDragging: true,
          binCode: clickedBin.binCode,
          startX: clickedBin.x,
          startY: clickedBin.y,
          mouseStartX: world.x,
          mouseStartY: world.y,
        });
      } else {
        if (state.selectedBinCode) {
          dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
        }
        if (!isMultiSelect) {
          // Start rubber band selection
          dispatch({ type: 'SET_RUBBER_BAND', rubberBand: startRubberBand(screenX, screenY) });
          dispatch({ type: 'SET_SELECTION', selectedRacks: new Set() });
        }
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!layout) return;

    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPanStart({ x: e.clientX, y: e.clientY });
      dispatch({
        type: 'SET_PAN_OFFSET',
        offset: { x: editorState.panOffset.x + dx, y: editorState.panOffset.y + dy },
      });
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Rubber band
    if (editorState.rubberBand.isActive) {
      dispatch({ type: 'SET_RUBBER_BAND', rubberBand: updateRubberBand(editorState.rubberBand, screenX, screenY) });

      const { startX, startY } = editorState.rubberBand;
      const world1 = screenToWorld(startX, startY, canvasSize.width, canvasSize.height, layout.dimensions, editorState.zoom, editorState.panOffset);
      const world2 = screenToWorld(screenX, screenY, canvasSize.width, canvasSize.height, layout.dimensions, editorState.zoom, editorState.panOffset);
      const selectedInArea = getRacksInArea(layout.racks, rackDimensionsMap, world1.x, world1.y, world2.x, world2.y);
      dispatch({ type: 'SET_SELECTION', selectedRacks: new Set(selectedInArea) });
      return;
    }

    // Drag av enkeltlokasjon
    if (binDragState.isDragging && binDragState.binCode) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const deltaX = world.x - binDragState.mouseStartX;
      const deltaY = world.y - binDragState.mouseStartY;
      dispatch({
        type: 'MOVE_BIN',
        binCode: binDragState.binCode,
        x: snapToGrid(binDragState.startX + deltaX, layout.gridUnit),
        y: snapToGrid(binDragState.startY + deltaY, layout.gridUnit),
      });
      return;
    }

    // Dragging
    if (dragState.isDragging && dragState.draggedRacks.length > 0) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const deltaX = world.x - dragState.mouseStartX;
      const deltaY = world.y - dragState.mouseStartY;

      const updates = dragState.draggedRacks
        .map(rackNumber => {
          const startPos = dragState.startPositions.get(rackNumber);
          if (!startPos) return null;
          return {
            rackNumber,
            x: snapToGrid(startPos.x + deltaX, layout.gridUnit),
            y: snapToGrid(startPos.y + deltaY, layout.gridUnit),
          };
        })
        .filter((u): u is NonNullable<typeof u> => u !== null);

      dispatch({ type: 'MOVE_RACKS', updates });
    }
  };

  const handleMouseUp = () => {
    if (editorState.rubberBand.isActive) {
      dispatch({ type: 'SET_RUBBER_BAND', rubberBand: resetRubberBand() });
    }
    setDragState({ isDragging: false, draggedRacks: [], startPositions: new Map(), mouseStartX: 0, mouseStartY: 0 });
    setBinDragState(IDLE_BIN_DRAG);
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, Math.min(5, editorState.zoom * delta)) });
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // --- Drag-and-drop from sidebar ---
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!layout || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const world = screenToWorld(
      e.clientX - rect.left,
      e.clientY - rect.top,
      canvasSize.width,
      canvasSize.height,
      layout.dimensions,
      editorState.zoom,
      editorState.panOffset
    );

    const rackNumber = e.dataTransfer.types.includes('text/plain') ? '__drag__' : '';
    if (!rackNumber) return;

    const snappedX = snapToGrid(world.x, layout.gridUnit);
    const snappedY = snapToGrid(world.y, layout.gridUnit);

    dispatch({
      type: 'SET_SIDEBAR_DRAG_PREVIEW',
      preview: { rackNumber: '?', x: snappedX, y: snappedY, rotation: 0, isValid: true },
    });
  }, [layout, canvasSize, editorState.zoom, editorState.panOffset, dispatch]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dispatch({ type: 'SET_SIDEBAR_DRAG_PREVIEW', preview: null });

    const rackNumber = e.dataTransfer.getData('text/plain');
    if (!rackNumber || !layout || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const world = screenToWorld(
      e.clientX - rect.left,
      e.clientY - rect.top,
      canvasSize.width,
      canvasSize.height,
      layout.dimensions,
      editorState.zoom,
      editorState.panOffset
    );

    const snappedX = snapToGrid(world.x, layout.gridUnit);
    const snappedY = snapToGrid(world.y, layout.gridUnit);

    dispatch({
      type: 'ADD_RACK',
      rack: { rackNumber, x: snappedX, y: snappedY, rotation: 0 },
    });
  }, [layout, canvasSize, editorState.zoom, editorState.panOffset, dispatch]);

  const handleDragLeave = useCallback(() => {
    dispatch({ type: 'SET_SIDEBAR_DRAG_PREVIEW', preview: null });
  }, [dispatch]);

  return (
    <div ref={containerRef} className="w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className="cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      />
    </div>
  );
};

export default Layout2DCanvas;
